import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { StaffMember, StaffRole, User } from "@backstory/protocol";
import { BusyButton, ConfirmDialog, Dialog, Field, PageHeader, RowMenu, useToast } from "../../components/ui";
import { ErrorState } from "../../components/States";
import { useAuth } from "../../auth/store";
import "./staff.css";
import { staffApi, staffKeys } from "./api";
import { useDebounced } from "./customers";

const ROLES: Array<{ value: StaffRole; label: string; help: string }> = [
  { value: "viewer", label: "Viewer", help: "Read the console. Cannot change anything." },
  { value: "operator", label: "Operator", help: "Run the platform: plans, credits, AI providers, support access." },
  { value: "admin", label: "Admin", help: "Everything an operator can do, plus managing this team." },
  { value: "owner", label: "Owner", help: "Owns the platform. Can grant ownership. The last owner cannot be removed." },
];

function roleWord(role: string): string {
  return ROLES.find((r) => r.value === role)?.label ?? role;
}

export function StaffTeamPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const auth = useAuth();
  const members = useQuery({ queryKey: staffKeys.members, queryFn: () => staffApi.members() });
  const [adding, setAdding] = useState(false);
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput.trim().toLowerCase(), 200);
  const mine = auth.me?.user.staffRole;
  const isManager = mine === "admin" || mine === "owner";
  const isOwner = mine === "owner";
  const assignable = ROLES.filter((r) => r.value !== "owner" || isOwner);

  const refresh = () => qc.invalidateQueries({ queryKey: staffKeys.members });
  const fail = (e: unknown) => toast.push("error", (e as Error).message);

  const setRoleFor = useMutation({
    mutationFn: (v: { userId: string; role: StaffRole }) => staffApi.setMemberRole(v.userId, v.role),
    onSuccess: () => void refresh(),
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => staffApi.removeMember(userId),
    onSuccess: () => {
      void refresh();
      toast.push("success", "Platform access revoked.");
    },
    onError: fail,
  });
  const cancel = useMutation({
    mutationFn: (mail: string) => staffApi.cancelInvitation(mail),
    onSuccess: () => {
      void refresh();
      toast.push("success", "Invitation cancelled.");
    },
    onError: fail,
  });

  const all = members.data?.items ?? [];
  const items = q ? all.filter((m) => m.email.toLowerCase().includes(q) || (m.name ?? "").toLowerCase().includes(q)) : all;

  return (
    <div>
      <PageHeader
        title="Platform team"
        subtitle="People who can open this console. Removing someone here leaves their own workspaces untouched."
        actions={
          isManager ? (
            <button className="btn primary" type="button" data-testid="add-staff" onClick={() => setAdding(true)}>
              Add to platform team
            </button>
          ) : undefined
        }
      >
        <div className="toolbar customers-filters" aria-label="Platform team filters">
          <input
            className="input customers-search"
            type="search"
            placeholder="Search by name or email"
            aria-label="Search platform team"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            autoComplete="off"
          />
          {members.data && (
            <span className="muted small">
              {all.length.toLocaleString()} {all.length === 1 ? "person" : "people"}
            </span>
          )}
        </div>
      </PageHeader>
      {members.isPending ? (
        <div className="table-scroll stack-table team-table">
          <table className="table" aria-busy="true">
            <tbody>
              {Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} aria-hidden="true">
                  <td>
                    <span className="skeleton" style={{ width: `${40 + ((i * 17) % 35)}%` }}>loading</span>
                  </td>
                  <td>
                    <span className="skeleton" style={{ width: 72 }}>loading</span>
                  </td>
                  <td>
                    <span className="skeleton" style={{ width: 48 }}>loading</span>
                  </td>
                  <td>
                    <span className="skeleton" style={{ width: 80 }}>loading</span>
                  </td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : members.isError ? (
        <ErrorState error={members.error} retry={() => void members.refetch()} />
      ) : items.length === 0 ? (
        <div className="card empty">
          <h3>{q ? "Nothing matches" : "No one on the platform team yet"}</h3>
          {q ? (
            <button type="button" className="btn ghost sm" onClick={() => setQInput("")}>
              Clear search
            </button>
          ) : (
            <p>Add the first person to give them this console.</p>
          )}
        </div>
      ) : (
        <TeamTable
          items={items}
          owners={all.filter((m) => m.role === "owner" && !m.pending).length}
          me={auth.me?.user}
          isManager={isManager}
          isOwner={isOwner}
          assignable={assignable}
          onRole={(userId, next) => setRoleFor.mutate({ userId, role: next })}
          onRemove={(userId) => remove.mutateAsync(userId)}
          onCancel={(mail) => cancel.mutateAsync(mail)}
        />
      )}
      <AddMemberDialog
        open={adding}
        onClose={() => setAdding(false)}
        assignable={assignable}
        onAdded={(m) => {
          void refresh();
          toast.push(
            "success",
            m.pending ? `Invited ${m.email}. They become a ${roleWord(m.role)} the first time they sign in.` : `${m.email} is now a platform ${roleWord(m.role)}.`,
          );
        }}
      />
    </div>
  );
}

function AddMemberDialog({
  open,
  onClose,
  assignable,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  assignable: Array<{ value: StaffRole; label: string; help: string }>;
  onAdded: (m: StaffMember) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<StaffRole>("operator");
  const [touched, setTouched] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const add = useMutation({
    mutationFn: () => staffApi.addMember({ email: email.trim(), role }),
    onSuccess: (m) => {
      onAdded(m);
      close();
    },
  });
  const close = () => {
    setEmail("");
    setRole("operator");
    setTouched(false);
    add.reset();
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title="Add to platform team"
      subtitle="Platform access is a second hat on a normal account: they sign in as themselves and gain this console."
      width={480}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={close} disabled={add.isPending}>
            Cancel
          </button>
          <BusyButton className="btn primary" busy={add.isPending} busyLabel="Adding…" disabled={!valid} data-testid="add-staff-submit" onClick={() => add.mutate()}>
            Add to platform team
          </BusyButton>
        </>
      }
    >
      <Field label="Email" error={touched && email && !valid ? "Enter a full email address." : undefined}>
        {(p) => (
          <input
            {...p}
            className="input"
            type="email"
            placeholder="operator@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            autoComplete="off"
            autoFocus
          />
        )}
      </Field>
      <Field label="Role" hint={assignable.find((r) => r.value === role)?.help}>
        {(p) => (
          <select {...p} className="input" value={role} onChange={(e) => setRole(e.target.value as StaffRole)}>
            {assignable.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        )}
      </Field>
      {add.isError && (
        <div className="error-box" role="alert">
          {(add.error as Error).message}
        </div>
      )}
    </Dialog>
  );
}

function TeamTable({
  items,
  owners,
  me,
  isManager,
  isOwner,
  assignable,
  onRole,
  onRemove,
  onCancel,
}: {
  items: StaffMember[];
  owners: number;
  me?: User;
  isManager: boolean;
  isOwner: boolean;
  assignable: Array<{ value: StaffRole; label: string }>;
  onRole: (userId: string, role: StaffRole) => void;
  onRemove: (userId: string) => Promise<unknown>;
  onCancel: (email: string) => Promise<unknown>;
}) {
  const meEmail = me?.email.toLowerCase();
  return (
    <div className="table-scroll stack-table team-table">
      <table className="table">
        <thead>
          <tr>
            <th>Person</th>
            <th>Role</th>
            <th>MFA</th>
            <th>Last sign-in</th>
            {isManager && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {items.map((m) => {
            const you = (!!me?.id && m.userId === me.id) || (!!meEmail && m.email.toLowerCase() === meEmail);
            const lastOwner = m.role === "owner" && !m.pending && owners <= 1;
            const canChange = isManager && !!m.userId && !m.pending && (m.role !== "owner" || isOwner) && !lastOwner;
            return (
              <MemberRow
                key={m.userId || m.email}
                m={m}
                you={you}
                canChange={canChange}
                canCancel={isManager && m.pending}
                showActions={isManager}
                assignable={assignable}
                onRole={onRole}
                onRemove={onRemove}
                onCancel={onCancel}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MemberRow({
  m,
  you,
  canChange,
  canCancel,
  showActions,
  assignable,
  onRole,
  onRemove,
  onCancel,
}: {
  m: StaffMember;
  you: boolean;
  canChange: boolean;
  canCancel: boolean;
  showActions: boolean;
  assignable: Array<{ value: StaffRole; label: string }>;
  onRole: (userId: string, role: StaffRole) => void;
  onRemove: (userId: string) => Promise<unknown>;
  onCancel: (email: string) => Promise<unknown>;
}) {
  const [confirm, setConfirm] = useState<"remove" | "cancel" | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      setConfirm(null);
    } catch {
      // The mutation already reported the failure.
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr>
      <td className="team-person-cell">
        <div className="team-person">
          <span className="team-person-name">{m.name || m.email}</span>
          {m.name && <span className="muted small">{m.email}</span>}
        </div>
        <span className="team-person-badges">
          {you && <span className="badge neutral">You</span>}
          {m.pending && <span className="badge neutral">Invited</span>}
        </span>
      </td>
      <td data-label="Role">
        {canChange ? (
          <select
            className="input"
            aria-label={`Role for ${m.email}`}
            autoComplete="off"
            value={m.role}
            onChange={(e) => onRole(m.userId as string, e.target.value as StaffRole)}
            style={{ maxWidth: 160 }}
          >
            {assignable.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        ) : (
          roleWord(m.role)
        )}
      </td>
      <td data-label="MFA">{mfaCell(m)}</td>
      <td className="muted" data-label="Last sign-in">
        {m.lastLoginAt ? new Date(m.lastLoginAt).toLocaleDateString() : "—"}
      </td>
      {showActions && (
        <td className="team-actions-cell">
          {canCancel ? (
            <RowMenu label={`Actions for ${m.email}`}>
              <button type="button" role="menuitem" className="danger" onClick={() => setConfirm("cancel")}>
                Cancel invite
              </button>
            </RowMenu>
          ) : canChange ? (
            <RowMenu label={`Actions for ${m.email}`}>
              <button type="button" role="menuitem" className="danger" onClick={() => setConfirm("remove")}>
                Remove from platform team
              </button>
            </RowMenu>
          ) : null}
          <ConfirmDialog
            open={confirm === "remove"}
            danger
            title="Remove from platform team"
            confirmLabel="Remove"
            busy={busy}
            onClose={() => setConfirm(null)}
            onConfirm={() => void run(() => onRemove(m.userId as string))}
          >
            {m.name || m.email} loses this console. Their own workspaces are untouched.
          </ConfirmDialog>
          <ConfirmDialog
            open={confirm === "cancel"}
            danger
            title="Cancel invitation"
            confirmLabel="Cancel invite"
            busy={busy}
            onClose={() => setConfirm(null)}
            onConfirm={() => void run(() => onCancel(m.email))}
          >
            {m.email} will not gain platform access when they sign in.
          </ConfirmDialog>
        </td>
      )}
    </tr>
  );
}

// The console refuses staff without a second factor, so an operator who has not
// enrolled one is listed but cannot act, and the table says so rather than
// leaving them to discover it on a 403.
function mfaCell(m: StaffMember) {
  if (m.pending) return <span className="muted">—</span>;
  if (m.mfaEnabled) return <span className="badge ok">On</span>;
  return <span className="badge warn">Not enrolled</span>;
}
